<template>
  <div v-if="!hideAddToCartBtn">

    <!-- Error Messages such as over limit -->
    <transition name="fade">
      <div class="product-card__error-bar" v-if="errorMessage" role="alert">
        <div class="alert mt-2 d-flex alert-secondary" data-message-type="error" v-html="errorMessage">
        </div>
      </div>
    </transition>

    <!-- Show Add to Cart Button. Maintain legacy logic. -->
    <div v-if="product.addToCartAllowed && product.productAvailableAtContext" class="product-card__btn-container">
      <!-- Add to Cart confirmation checkmark -->
      <transition name="fade">
        <div v-if="showCheckmark" class="btn btn-add-to-cart add-to-cart-button__checkmark-container">
          <span
            class="fa fa-check"
            aria-label="Adding to cart">
          </span>
        </div>
      </transition>

    <div v-show="quantityInBucket" class="add-to-bucket__x-added">
      {{ frontendItem.quantity }}
      <span v-if="product.pricedByWeight">&nbsp;{{
        product.defaultSku.priceInfo.listPrice.unit
      }} </span>
      <span v-if="frontendItem.quantity.toString().length < 3">&nbsp;added</span>
    </div>

      <!-- Decrement button -->
      <button
        v-if="quantityInBucket > 0"
        @click.prevent="decrement(product.id);"
        class="btn btn-decrement"
        :class="`btn-decrement--${size}`"
        :aria-label="removeButtonAriaLabel"
      >
        <span class="fa fa-minus"></span>
      </button>

      <div class="add-to-cart-button-container">
        <button
          :tabindex="buttonTabIndex"
          @click.prevent="increment(product.id);"
          class="btn btn-add-to-cart"
          v-bind:class="[
            iconAfter,
            quantityInBucket !== 0 ? 'isMinusable' : '',
            product.addToCartDisabled ? 'disabled' : '',
            preventAddToBucket ? 'disabled' : '',
            maximumOrderLimitReached ? 'maximumOrderLimitReached' : '',
            'btn-add-to-cart--' + size,
            'icon__before-cart--' + size
          ]"
          v-bind:disabled="product.addToCartDisabled || preventAddToBucket"
          :aria-label="addButtonAriaLabel"
          aria-live="assertive"
        >
          <span v-show="!quantityInBucket"
            >Add {{ minimumOrderQuantityDisplayText }}to {{ bucketDisplayName }}</span
          >
        </button>
        <!-- Weight selector -->
        <div class="weight-select-container" v-if="product.pricedByWeight">
          <select
            tabindex="0"
            class="weight-select"
            @change.prevent="addItemToBucket"
            v-model="weight"
          >
            <option value="0" selected="selected" disabled
              >Select a Weight</option
            >
            <option
              v-for="(weight, index) in product.defaultSku
                .weightSelectionIncrements"
              v-bind:key="index"
              v-bind:value="weight"
              >{{ weight }}
              {{ product.defaultSku.priceInfo.listPrice.unit }}</option
            >
          </select>
        </div>
      </div>
    </div>

    <!-- Show Sold Out Online disabled button -->
    <div v-if="!product.addToCartAllowed && product.productAvailableAtContext === false" class="product-card__btn-container">
      <button disabled class="btn btn-add-to-cart disabled">
        Not Available
      </button>
    </div>

    <!-- Show Sold Out Online disabled button -->
    <div v-if="!product.addToCartAllowed && product.productAvailableAtContext === true" class="product-card__btn-container">
      <button disabled class="btn btn-add-to-cart disabled">
        Sold Out Online
      </button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import errorMessaging from './mixins/errorMessaging';
import { PRODUCT_ADD_TO_CART_EVENT, PRODUCT_FIRST_ADD_TO_CART_EVENT } from "../utilities/constants";

export default {
  name: "AddToBucket",
  mixins: [errorMessaging],
  props: {
    product: {
      type: Object
    },
    bucket: {
      type: String,
      default: "cart" // cart, orderAddOns. Used for figuring out how many are in your bucket
    },
    size: {
      type: String,
      default: "normal" // normal, large
    },
    listname: {
      type: String,
      default: ""
    },
    buttonType: {
      type: String,
      default: "spa" // spa, nonspa
    },
    hideAddToCartBtn: {
      type: Boolean,
      default: false
    },
    position: {
      type: Number,
      default: ""
    }
  },
  data() {
    return {
      weight: 0,
      couponIdDiv: null,
      showCheckmark: false,
      errorMessage: false,
      frontendItem: { //local copy for doing frontend modifications that will be debounced and ajaxed
        quantity: 0
      }
    };
  },
  computed: {
    minimumOrderQuantityDisplayText() {
      var min = _.get(this, 'product.minimumOrderQuantity', 1)
      if ( min > 1) {
        return `${min} `;
      } else {
        return "";
      }
    },
    quantity() {
      return this.quantityInBucket < this.product.minimumOrderQuantity ? this.product.minimumOrderQuantity : 1;
    },
    commerceItem() {
      if (typeof this.$store.state[this.bucket] === "undefined") {
        return;
      }
      var commerceItem = 0;

      // Look for cart commerce item by product.id
      if (this.bucket === "cart") {
        var vm = this;

        this.$store.state.cart.commerceItems.forEach(function(category) {
          var item = category.items.find(function(item) {
            return item.product.id === vm.product.id;
          });

          if (item) {
            commerceItem = item;
          }
        });

        return commerceItem;
      }

      // Lookup order add on commerce item by product.id
      if (this.bucket === "orderAddOns") {
        var vm = this;

        var item = this.$store.state.orderAddOns.orderAddOn.commerceItems.find(
          function(item) {
            return item.product.id === vm.product.id;
          }
        );

        if (item) {
          commerceItem = item;
        }

        return commerceItem;
      }
    },
    quantityInBucket() {
      var qty = 0;

      if (this.commerceItem && !this.product.pricedByWeight) {
        qty = this.commerceItem.quantity;
      }

      if (this.commerceItem && this.product.pricedByWeight) {
        qty = this.commerceItem.estimatedWeight;
      }

      return qty;
    },
    iconAfter() {
      if (this.product.pricedByWeight) {
        return "icon__after-down--" + this.size;
      } else if (this.product.addToCartAllowed && this.quantityInBucket) {
        return "icon__after-plus--" + this.size;
      }
    },
    bucketDisplayName() {
      switch (this.bucket) {
        default:
          return "cart";
        case "cart":
          return "cart";
        case "orderAddOns":
          return "order";
      }
    },
    preventAddToBucket() {
      return (
        this.bucket === "orderAddOns" &&
        this.$store.state.orderAddOns.orderAddOn.remainingAddOnItems === 0
      );
    },
    maximumOrderLimitReached(){
      return this.frontendItem.quantity >= _.get(this, 'product.maximumOrderQuantity', this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY)
    },
    addButtonAriaLabel() {
      if (!this.quantityInBucket) {
        if (this.product.pricedByWeight) {
          return `Select Weight to add ${this.minimumOrderQuantityDisplayText} to ${
            this.bucketDisplayName
          }`;
        }
        return `Add ${this.minimumOrderQuantityDisplayText} To ${this.bucketDisplayName}.`;
      } else {
        if (this.product.pricedByWeight) {
          return `${this.quantityInBucket} ${
            this.product.defaultSku.priceInfo.listPrice.unit
          } in cart.`;
        } else {
          return `${this.quantityInBucket} in cart. Add ${this.minimumOrderQuantityDisplayText} To ${
            this.bucketDisplayName
          }.`;
        }
      }
    },
    removeButtonAriaLabel() {
      if (this.product.pricedByWeight) {
        return `Remove ${this.quantityInBucket} ${
          this.product.defaultSku.priceInfo.listPrice.unit
        } from ${this.bucketDisplayName}`;
      }
      return `Remove ${this.quantity} from ${this.bucketDisplayName}.`;
    },
    buttonTabIndex() {
      if (this.product.pricedByWeight) {
        return `-1`;
      }
      return `0`;
    }
  },
  methods: {
    addItemToBucket() {
      if (this.product.pricedByWeight) {
        var item = {
          productId: this.product.id,
          skuId: this.product.defaultSku.id,
          quantity: this.quantity,
          weight: this.weight
        };
      } else {
        var item = {
          productId: this.product.id,
          skuId: this.product.defaultSku.id,
          quantity: this.quantity
        };
      }
      //If there is no store selected by the user and the fulfillment type is both (5003) or instorepickup (5001), pop the store selector in STH or EMS
      if (
        !window.heb.data.user.isStoreSelected &&
        (this.product.fulfillmentChannel === 5001 ||
          this.product.fulfillmentChannel === 5003)
      ) {
        loadPickupStore(
          heb.data.user.postalCode,
          true,
          item.skuId,
          item.productId,
          "addToCart"
        );
        return;
      }

      // checkmark is only shown when adding a non-weighted item to the cart
      // for the first time; it will not show when incrementing/decrementing quantities
      this.showCheckmark = !this.product.pricedByWeight;

      // Clear any inline error message
      this.errorMessage = false


      // Ask users if they are sure they want to shop at Vic3 (defaultStore) on first ATC
      if (this.$store.state.cart.commerceItemCount === 0 && this.$store.state.cart.curbsideFulfillmentMode === 'pickup' && !window.heb.data.user.isStoreSelected){
        if (window.__EXORDX__) {
          window.__EXORDX__.dispatch({
            type: "@@HEB/MODALS/SHOW_MODAL",
            payload: {
              modal: "CONFIRM_SHOPPING_AT_DEFAULT_STORE_MODAL",
            }
          });
        }
      }
      
      this.$store.dispatch(this.bucket + "/ADD_ITEM_TO_BUCKET", item)
        .then((response) => {

          // Reset back to previous quantity on error
          if(response.status !== 200){
            this.frontendItem.quantity = this.commerceItem.quantity || 0
            this.errorMessage = this.getShortErrorMessage(response);
          }

          // Successful add-to-cart
          if(response.status == 200){
            // Show representative product modal
            if(_.get(this, "product.isBestAvailable", false)){
              if(this.$route){
                this.hupOpenModal({modalId: 'representative-product-modal', closeAction: 'back'})
              }else{
                this.$store.commit('profile/OPEN_MODAL', {
                  id: 'representative-product-modal'
                })
              }
            }

            // this code finds the one-to-one coupon card (if present) and displays it.
            // TODO: phase DOM lookup out when product-card is 100% vue
            var couponCardInDom = document.getElementById(
              `${this.product.id}-${this.product.defaultSku.availableCouponIds[0]}`);
            if (this.product.defaultSku.availableCouponIds.length > 0 && couponCardInDom) {
              console.log(
                `${this.product.id}-${this.product.defaultSku.availableCouponIds[0]}`
              );
              couponCardInDom.style.display = "block";
            }

            // Show coupon one-to-one card, Vue version
            if(this.product.defaultSku.availableCouponIds.length === 1){
              this.$emit('show-coupon-one-to-one-card')
            }

            this.gtmClick(
              PRODUCT_FIRST_ADD_TO_CART_EVENT,
              this.product,
              this.bucket,
              this.listname,
              item.quantity,
              this.buttonType,
              this.position,
              "default",
              {
                isFirstAddToCart: true
              }
            );
          } // End Successful add to cart

          // show the checkmark for a minimum of .2s so it looks smooth on fast connections
          setTimeout(() => {
            this.showCheckmark = false;
          }, 200);

        });

      this.gtmClick(
        PRODUCT_ADD_TO_CART_EVENT,
        this.product,
        this.bucket,
        this.listname,
        item.quantity,
        this.buttonType,
        this.position,
        "default"
      );
    },
    decrement() {
      this.errorMessage = false
      if (this.product.pricedByWeight) {
        this.weight = 0;
        this.deleteItem(this.commerceItem.id);
        return;
      }

      if (this.frontendItem.quantity <= 1 || this.frontendItem.quantity <= this.product.minimumOrderQuantity) {
        this.deleteItem(this.commerceItem.id);
        return;
      }

      if (
        this.product.minimumOrderQuantityProduct &&
        this.quantityInBucket == this.product.minimumOrderQuantity
      ) {
        this.deleteItem(this.commerceItem.id);
        return;
      }

      if (
        this.quantityInBucket <= this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY
      ) {
        if (document.getElementById("shopListLimitError")) {
          document
            .getElementById("shopListLimitError")
            .classList.add("dontShow");
        }
      }

      this.frontendItem.quantity--
      this.debouncedPutQuantity();
    },
    increment() {
      this.errorMessage = false
      if (this.product.pricedByWeight && this.weight === 0) {
        // HUP-3778
        // Changing weight should not be handled by this function but
        // v-bind:tabindex=-1 is not working as expected
        return;
      }

      if (!this.product.addToCartDisabled && !this.preventAddToBucket) {
        if (this.quantityInBucket == 0) {
          this.addItemToBucket();
          return;
        }

        if (
          this.frontendItem.quantity >= this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY
        ) {
          if (document.getElementById("shopListLimitError")) {
            let messageTarget = document
              .getElementById("shopListLimitError")
              .getElementsByClassName("text")[0];
            messageTarget.textContent =
              "This product has reached its maximum order quantity";
            document
              .getElementById("shopListLimitError")
              .classList.remove("dontShow");
          }
          return;
        }

        // Stop incrementing at product.maximumOrderQuantity
        if(this.maximumOrderLimitReached){
          this.errorMessage = this.$store.state.config.constants.ERROR_MAX_ORDER_QUANTITY // Quantity limit reached
          return
        }

        this.frontendItem.quantity++
        this.debouncedPutQuantity();
      }
    },
    deleteItem: function(id) {
      this.$store.dispatch(this.bucket + "/DELETE_ITEM", id);
    },
  },
  created() {
    this.debouncedPutQuantity = _.debounce(function () {
      if(this.frontendItem.quantity > 0){
        // Clear errors when trying again
        this.errorMessage = false

        this.$store.dispatch(this.bucket + '/PUT_ITEM_QUANTITY', {...this.commerceItem, ...this.frontendItem}).then((response) => {

          // Reset back to previous quantity on error
          if(response.status !== 200){
            this.frontendItem.quantity = this.commerceItem.quantity || 0
            this.errorMessage = this.getShortErrorMessage(response);

            return
          }
        })

        this.gtmClick(
          PRODUCT_ADD_TO_CART_EVENT,
          this.product,
          this.bucket,
          this.listname,
          this.frontendItem.quantity,
          this.buttonType,
          this.position,
          "default"
        );
      }
    }, 500)
  },
  watch: {
    // debounce helper
    quantityInBucket: {
      immediate: true,
      handler(newValue, oldValue) {
        this.frontendItem.quantity = newValue
      },
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../assets/lib";
@import "../../../static-assets/css/_btn.scss";
@import "../../../static-assets/css/_product-card.scss";

.product-card__btn-container {
  display: flex;
  border-radius: 4px;
  position: relative;
  font-size: 14px;
  color: #fff;
}

.product-card__btn-container,
.product-card__btn-container button{
    font-weight: 700;
}

.btn-add-to-cart {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  width: 100%;
  -webkit-box-orient: horizontal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-align: center;
  align-items: center;
  -ms-flex-origin: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-justify-content: center;
  .fa {
    font-size: 16px;
  }
}

.add-to-bucket__x-added {
  position: absolute;
  z-index: 5;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.add-to-cart-button__checkmark-container {
  position: absolute;
  z-index: 10;
}

.icon__after-plus--normal {
  &:after {
    font-family: "FontAwesome";
    content: "\f067";
    position: absolute;
    right: 10px;
    top: 0;
    line-height: 38px;
  }
}

.icon__after-plus--normal.maximumOrderLimitReached:after,
.icon__after-plus--large.maximumOrderLimitReached:after{
  opacity: .5;
}

.icon__after-plus--large {
  &:after {
    font-family: "FontAwesome";
    content: "\f067";
    position: absolute;
    right: 0.5em;
    top: 0;
    line-height: 52px;
  }
}

.icon__after-down--normal {
  &:after {
    font-family: "FontAwesome";
    content: "\f078";
    position: absolute;
    right: 0.5em;
    top: 0;
    line-height: 38px;
    z-index: 10;
    pointer-events: none;
  }
}

.icon__after-down--large {
  &:after {
    font-family: "FontAwesome";
    content: "\f078";
    position: absolute;
    right: 0.5em;
    top: 0;
    line-height: 52px;
  }
}

button.btn.btn-add-to-cart.isMinusable {
  padding-right: 1.5em;
}

.btn-add-to-cart,
.btn-decrement {
  border: 0;
  margin: 0;
  color: #fff;
  background-color: $color-heb-primary;
  height: 38px;

  &:hover {
    background-color: darken($color-heb-primary, 10%);
  }
}

// .weight-select-container:hover{
//   background-color: darken($color-heb-primary, 10%);
//   transition: background-color 300ms ease-out;
// }

.btn-add-to-cart {
  flex-grow: 1;
  flex-basis: 100%;
  padding: 8px 1rem;
}

.btn-decrement {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding: 0 10px;
}

.fade-enter-active {
  transition: opacity 0.3s ease-out;
}
.fade-leave-active {
  transition: opacity 0.2s ease-in;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

@media screen and (min-width: $breakpoint-tablet) {
  .btn-add-to-cart--large,
  .btn-decrement--large {
    height: 52px;
    font-size: 1rem;
  }
}

@media screen and (max-width: $breakpoint-tablet) {
  .icon__after-down--large {
    &:after {
      line-height: 38px;
    }
  }

  .icon__after-plus--large {
    &:after {
      line-height: 38px;
    }
  }
}
.weight-select-container {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: visible;
}

// Invisible button
// Uses system default :focus color to highlight for keyboard users
select.weight-select {
  min-width: auto;
  opacity: 0;
  border: 0;
  z-index: 1;
  max-width: unset;
}

.product-card__error-bar .alert{
  font-size: 14px;
  font-weight: normal;
  flex-grow: 1;
  padding: .75rem;
  justify-content: center;
}
</style>
